/**
 * Feature Flags Utility
 *
 * Controls which features are available in each environment.
 * This is the standard approach used by large software companies to:
 * - Gradually roll out new features
 * - Test features in QA before production
 * - Enable/disable features without code changes
 *
 * Environment is determined by the Supabase URL in the build.
 */

// Determine current environment based on Supabase URL
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";
// Production may be reached via the raw Supabase host OR the custom domain
// `crmapi.uslawshield.com` (added during the 2026-06-08 DNS-incident fix). Both
// must count as production, otherwise prod falls through to the "development"
// config and prod-specific feature flags don't apply.
export const isProduction =
  SUPABASE_URL.includes("nnzheppmjkoygpdbrqkj") ||
  SUPABASE_URL.includes("crmapi.uslawshield.com");
export const isQA = SUPABASE_URL.includes("qqynnldqhtstlfiimnwm");

export type Environment = "production" | "qa" | "development";

export const currentEnvironment: Environment = isProduction
  ? "production"
  : isQA
    ? "qa"
    : "development";

/**
 * Feature flag definitions
 *
 * For each feature, specify which environments it's enabled in.
 * Features default to enabled in QA and development, disabled in production.
 */
interface FeatureFlags {
  // Partner Offers Dashboard - still in development
  partnerOffersDashboard: boolean;

  // Issue Reward button on Redemptions page - still in development
  issueRewardButton: boolean;

  // Support Tasks Dashboard - still in development
  supportTasksDashboard: boolean;

  // Document Chat / RAG features - requires database migrations
  documentChat: boolean;

  // Webpage Chat features - requires database migrations
  webpageChat: boolean;

  // Chat Analytics - requires database migrations
  chatAnalytics: boolean;

  // Contact Roles feature - requires database migrations
  contactRoles: boolean;

  // Operator SMS — composer, review queue and STOP ledger. The SMS schema, edge functions and
  // Sinch secrets are live in production as of 2026-09-25.
  operatorSms: boolean;

  // ⭐ SEPARATE from operatorSms on purpose: whether a composed message is actually HANDED TO THE
  // PROVIDER. operatorSms controls whether the UI exists; this controls whether it sends. QA shares
  // production's Sinch service plan and short code, so sending from QA would text real people on
  // the real short code — QA therefore shows the full composer and refuses to send.
  operatorSmsSending: boolean;

  // Picture messages (MMS) in the SMS composer: Attach image / paste. The server switch
  // (SINCH_MMS_ENABLED) decides whether images can actually be SENT; this decides whether the controls
  // are SHOWN, so production never displays an image option that cannot work (off until Sinch enables
  // MMS on the account -- docs/MMS_RELEASE_PLAN_2026-09-28.md).
  operatorMms: boolean;

  // Two-way SMS conversations (inbox /messages, thread view, contact "SMS messages" tab) — READ side
  // only; replies are not built. This flag only decides whether the UI ASKS the server: the real
  // gate is the sms_conversations_available() RPC (role + feature/internal-proof allowlist), and no
  // entry point renders unless it answers true. Plan: docs/PLAN_SMS_TWO_WAY_2026-09-30.md §9.
  operatorSmsConversations: boolean;

  // Geographic Search - requires database migrations
  geographicSearch: boolean;

  // Sales Team Management - hierarchy, territories, account reviews
  salesTeamManagement: boolean;

  // Sales Calendar - calendar view of rep events and availability
  salesCalendar: boolean;

  // Form Handlers - public lead capture endpoints from 3rd-party form builders
  formHandlers: boolean;

  // Event ticketing (ticket tiers tab). The event_ticket_tiers table + supporting
  // functions are not yet deployed to production, so the Tickets tab errors there.
  // Stopgap: hide the tab in production until the ticketing feature is promoted.
  eventTicketing: boolean;

  // NeverBounce Former/Cancelled-Member background validation CAMPAIGN (admin-only surface). This is a
  // high-cost, money-adjacent one-time backfill. DARK by DEFAULT in ALL environments (including QA)
  // until the campaign schema/edge fns/secrets are deployed and it passes its independent GO/NO-SHIP
  // audit AND the business approves a pilot. Never flip on without the runbook's activation gates.
  neverbounceCampaignBackfill: boolean;

  // Email the events team (Events@uslawshield.com) when the create wizard saves
  // an event as setup_incomplete, so they can finish configuring it. Best-effort
  // heads-up; the events list also badges incomplete events as a backstop. OFF in
  // production until the business confirms the recipient + copy.
  incompleteEventNotify: boolean;

  // Attorney question routing (agent surface + admin coverage table). DARK in production
  // until the routing engine and attorney portal exist: until then an agent could file a
  // member's legal question into a queue that never dispatches, which looks handled and is
  // not. The flag also decouples this from the branch ladder — anything on `qa` reaches PROD
  // on the next unrelated Gate 3, so the routes must be inert there by configuration, not by
  // hoping nobody merges.
  attorneyQuestionRouting: boolean;

  // Finance expenses (event Budget & Expenses tab, /finance pages): anticipated expenses, documents, approvals.
  // DARK in production until Finance answers the open questions and the external audit passes
  // (docs/PLAN_FINANCE_EXPENSE_INVOICE_2026-10-21.md). This flag only decides whether the UI ASKS the server: the
  // real gate is finance_settings.module_enabled (migration 970), and nothing renders unless finance_my_access()
  // answers enabled + staff.
  financeExpenses: boolean;

  // Events CSV import (/events/import): bulk-create gun shows and range nights from a spreadsheet. DARK in production
  // until migration 980 (event_csv_import_* RPCs) is applied on PROD and the owner approves after the external audit;
  // the PROD release flips this to true in the same release (database first, then this frontend).
  eventCsvImport: boolean;
}

/**
 * Feature flag configuration by environment
 */
const featureFlagConfig: Record<Environment, FeatureFlags> = {
  production: {
    // Features enabled in production (Feb 2026 release)
    partnerOffersDashboard: true,
    issueRewardButton: true,
    supportTasksDashboard: true,
    documentChat: true,
    webpageChat: true,
    chatAnalytics: true,
    contactRoles: true,
    geographicSearch: true,
    salesTeamManagement: true,
    salesCalendar: true,
    formHandlers: true,
    // event_ticket_tiers not on production yet — hide the Tickets tab (stopgap).
    eventTicketing: false,
    neverbounceCampaignBackfill: false, // DARK everywhere until audit + pilot approval (see featureFlags interface)
    attorneyQuestionRouting: false, // DARK in PROD until slices 3-4 (routing + attorney portal)
    eventCsvImport: true, // PROD ACTIVATED 2026-10-22 with mig 980 (external audit GO round 2, code 82077432)
    incompleteEventNotify: true, // PROD ACTIVATED 2026-08-20 — recipients business-confirmed (events@uslawshield.com; temp second recipient removed 2026-10-02)
    // ⭐ ACTIVATED 2026-09-25 after the first end-to-end send: consent gate -> Sinch 201 ->
    // delivery callback confirmed "Delivered" in 3s. Edge functions and Sinch secrets are live.
    operatorSms: true,
    // One-way only by owner decision: no inbound poller is scheduled, so a member's STOP reply is
    // honoured by the carrier at the short code but is NOT recorded in our ledger.
    operatorSmsSending: true,
    operatorMms: false,
    operatorSmsConversations: false, // DARK in PROD: internal proof runs on QA first
    financeExpenses: true, // UI may ASK; the server gate decides: finance_settings.module_enabled (false until switch_on) + module_roles (finance, superadmin). Unhide = DB switch only.
  },
  qa: {
    // All features enabled in QA for testing
    partnerOffersDashboard: true,
    issueRewardButton: true,
    supportTasksDashboard: true,
    documentChat: true,
    webpageChat: true,
    chatAnalytics: true,
    contactRoles: true,
    operatorSms: true,
    // NOT true: QA shares production's short code — see the interface comment.
    operatorSmsSending: false,
    operatorMms: true,
    operatorSmsConversations: true, // server probe is still the gate (allowlist)
    geographicSearch: true,
    salesTeamManagement: true,
    salesCalendar: true,
    formHandlers: true,
    eventTicketing: true,
    neverbounceCampaignBackfill: false, // DARK until audit + pilot approval
    attorneyQuestionRouting: true, // QA: the surface under construction, for tester feedback
    eventCsvImport: true, // QA: mig 980 applied 2026-10-21
    incompleteEventNotify: true, // QA: exercise the notify path
    financeExpenses: true, // QA: server switch finance_settings.module_enabled is the real gate
  },
  development: {
    // All features enabled in development
    partnerOffersDashboard: true,
    issueRewardButton: true,
    supportTasksDashboard: true,
    documentChat: true,
    webpageChat: true,
    chatAnalytics: true,
    contactRoles: true,
    operatorSms: true,
    // NOT true: QA shares production's short code — see the interface comment.
    operatorSmsSending: false,
    operatorMms: true,
    operatorSmsConversations: true, // server probe is still the gate (allowlist)
    geographicSearch: true,
    salesTeamManagement: true,
    salesCalendar: true,
    formHandlers: true,
    eventTicketing: true,
    neverbounceCampaignBackfill: false, // DARK until audit + pilot approval
    attorneyQuestionRouting: true, // dev
    eventCsvImport: true,
    incompleteEventNotify: true, // dev: exercise the notify path
    financeExpenses: true, // dev
  },
};

/**
 * Get the current feature flags based on environment
 */
export const featureFlags: FeatureFlags = featureFlagConfig[currentEnvironment];

/**
 * Check if a specific feature is enabled
 */
export function isFeatureEnabled(feature: keyof FeatureFlags): boolean {
  return featureFlags[feature];
}

/**
 * Get all enabled features (useful for debugging)
 */
export function getEnabledFeatures(): (keyof FeatureFlags)[] {
  return (Object.keys(featureFlags) as (keyof FeatureFlags)[]).filter(
    (key) => featureFlags[key],
  );
}

/**
 * Log current environment and feature flags (for debugging)
 */
export function logFeatureFlags(): void {
  console.log("[FeatureFlags] Environment:", currentEnvironment);
  console.log("[FeatureFlags] Enabled features:", getEnabledFeatures());
}

export default featureFlags;
